import React, { useEffect, useState } from "react";
import ApiConfig from "../../common/config/ApiConfig";
import { useLanguage } from "../../common/i18n/LanguageContext";

const formatAmount = (value) => {
  const floatValue = parseFloat(value);
  return isNaN(floatValue)
    ? "-"
    : floatValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

function LottoWaitResultUser({ data = [], date }) {
  const { t } = useLanguage();
  const [seniorSel, setSeniorSel] = useState(null);
  const [masterSel, setMasterSel] = useState(null);
  const [agentSel, setAgentSel] = useState(null);
  const [currencyCodeById, setCurrencyCodeById] = useState({});

  useEffect(() => {
    ApiConfig.get("/getCurrencyList").then((result) => {
      if (result.resCode !== 200) return;
      const list = Array.isArray(result.data) ? result.data : [];
      setCurrencyCodeById(Object.fromEntries(list.map((c) => [c.id, c.code])));
    });
  }, []);

  // Each row (senior/master/agent/member) carries its own currency_id.
  const withCurrency = (text, row) => {
    const code = currencyCodeById[row.currency_id];
    return code && text !== "-" ? `${text} ${code}` : text;
  };

  let level = "senior";
  let rows = data;
  if (seniorSel) {
    level = "master";
    rows = seniorSel.masters || [];
  }
  if (masterSel) {
    level = "agent";
    rows = masterSel.agents || [];
  }
  if (agentSel) {
    level = "member";
    rows = agentSel.members || [];
  }

  const resetToSenior = () => {
    setSeniorSel(null);
    setMasterSel(null);
    setAgentSel(null);
  };
  const resetToMaster = () => {
    setMasterSel(null);
    setAgentSel(null);
  };
  const resetToAgent = () => {
    setAgentSel(null);
  };

  // Member level opens winlose-detail in a new tab instead of expanding
  // inline, so the outstanding-by-user table stays put behind it.
  const handleRowClick = (row) => {
    if (level === "senior") setSeniorSel(row);
    else if (level === "master") setMasterSel(row);
    else if (level === "agent") setAgentSel(row);
    else if (level === "member") {
      const params = new URLSearchParams();
      params.set("username", row.username);
      if (date) {
        params.set("startdate", date);
        params.set("enddate", date);
      }
      window.open(`/outstanding-detail?${params.toString()}`, "_blank");
    }
  };

  const showSeniorCol = level === "senior";
  const showMasterCol = level === "senior" || level === "master";
  const showAgentCol = level !== "member";
  const colCount =
    4 +
    (showSeniorCol ? 1 : 0) +
    (showMasterCol ? 1 : 0) +
    (showAgentCol ? 1 : 0);

  return (
    <div className="overflow-x-auto rounded">
      <div className="text-sm mb-2 flex flex-wrap items-center gap-1 user-level">
        <button
          type="button"
          onClick={resetToSenior}
          className={`hover:underline ${
            level === "senior"
              ? "font-bold text-brand-blue-800"
              : "text-slate-600"
          }`}
        >
          Senior{seniorSel ? `: ${seniorSel.username}` : ""}
        </button>
        {seniorSel && (
          <>
            <span className="text-slate-400">\</span>
            <button
              type="button"
              onClick={resetToMaster}
              className={`hover:underline ${
                level === "master"
                  ? "font-bold text-brand-blue-800"
                  : "text-slate-600"
              }`}
            >
              Master{masterSel ? `: ${masterSel.username}` : ""}
            </button>
          </>
        )}
        {masterSel && (
          <>
            <span className="text-slate-400">\</span>
            <button
              type="button"
              onClick={resetToAgent}
              className={`hover:underline ${
                level === "agent"
                  ? "font-bold text-brand-blue-800"
                  : "text-slate-600"
              }`}
            >
              Agent{agentSel ? `: ${agentSel.username}` : ""}
            </button>
          </>
        )}
      </div>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-brand-blue-800 text-white">
            <th className="px-3 py-2 text-center">{t("membertable_col_no")}</th>
            <th className="px-3 py-2 text-left">{t("winlose_col_account")}</th>
            <th className="px-3 py-2 text-right">
              {t("waitresult_bet_wager_label")}
            </th>
            <th className="px-3 py-2 text-right">
              {t("waitresult_bet_payout_label")}
            </th>
            {showSeniorCol && (
              <th className="px-3 py-2 text-left">
                {t("waitresult_amount_label")}(Senior)
              </th>
            )}
            {showMasterCol && (
              <th className="px-3 py-2 text-left">
                {t("waitresult_amount_label")}(Master)
              </th>
            )}
            {showAgentCol && (
              <th className="px-3 py-2 text-left">
                {t("waitresult_amount_label")}(Agent)
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="text-center py-4 text-gray-400">
                {t("common_no_data")}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={row.username ?? index}
                onClick={() => handleRowClick(row)}
                className={`cursor-pointer hover:bg-brand-blue-50 ${
                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                }`}
              >
                <td className="px-3 py-2 text-center text-gray-500">
                  {index + 1}
                </td>
                <td className="px-3 py-2">
                  <span className="inline-block px-2 py-1 rounded-lg bg-brand-blue-700 text-white font-bold text-sm drop-shadow-xl">
                    {row.username}
                  </span>
                  {row.f_name && (
                    <span className="ml-2 text-sm text-gray-500">
                      {row.f_name} {row.l_name}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-semibold text-sky-600">
                  {withCurrency(formatAmount(row.bet_amount), row)}
                </td>
                <td className="px-3 py-2 text-right font-semibold text-red-600">
                  {withCurrency(formatAmount(row.payrateAmount), row)}
                </td>
                {showSeniorCol && (
                  <td className="px-3 py-2 text-gray-700">
                    {withCurrency(row.seniorOutstanding ?? "-", row)}
                  </td>
                )}
                {showMasterCol && (
                  <td className="px-3 py-2 text-gray-700">
                    {withCurrency(row.masterOutstanding ?? "-", row)}
                  </td>
                )}
                {showAgentCol && (
                  <td className="px-3 py-2 text-gray-700">
                    {withCurrency(row.agentOutstanding ?? "-", row)}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default LottoWaitResultUser;
